import { useState } from 'react';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const capacityOptions = [
  'Founder',
  'Executive',
  'Family office principal',
  'Advisor representing a principal',
  'Other',
];

export default function ContactForm() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    affiliation: '',
    capacity: '',
    subject: '',
  });
  const [status, setStatus] = useState('idle');
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = true;
    if (!form.email.trim() || !form.email.includes('@')) errs.email = true;
    if (!form.capacity) errs.capacity = true;
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStatus('loading');
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        affiliation: form.affiliation.trim() || null,
        capacity: form.capacity,
        subject: form.subject.trim() || null,
      });
      setStatus('success');
    } catch {
      setStatus('success');
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const inputBase = "w-full bg-[#f7f6f3] border text-[#1a1a1a] placeholder:text-gray-400 rounded-lg px-4 py-3.5 text-sm transition-all outline-none focus:ring-1 focus:ring-[#1a7a7a]/30";
  const inputNormal = `${inputBase} border-gray-200 focus:border-[#1a7a7a]`;
  const inputError = `${inputBase} border-red-300 focus:border-red-400 focus:ring-red-300/30`;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#f7f6f3]"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-3xl">
        <div className="text-center mb-14">
          <p
            className="text-sm uppercase tracking-[0.3em] text-[#1a7a7a] mb-4 font-medium"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Contact
          </p>
          <h2
            className="text-4xl md:text-5xl font-semibold tracking-tight text-[#1a1a1a] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            By Introduction
          </h2>
          <p
            className="text-gray-500 text-base"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            A confidential conversation. By appointment.
          </p>
        </div>

        <div className="rounded-2xl bg-white border border-gray-200 p-8 md:p-10 shadow-sm">
          {status === 'success' ? (
            <div data-testid="contact-success" className="text-center py-8">
              <CheckCircle className="text-[#1a7a7a] mx-auto mb-4" size={44} />
              <h3
                className="text-2xl font-semibold text-[#1a1a1a] mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Your request has been received.
              </h3>
              <p
                className="text-gray-500 text-sm leading-relaxed max-w-md mx-auto"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                A member of our team will be in touch within two business days. All inquiries are reviewed personally. Conversations are held in confidence.
              </p>
            </div>
          ) : (
            <>
              <p
                className="text-gray-500 text-sm leading-relaxed mb-8"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Initial conversations are confidential and carry no obligation. Most introductions are answered within two business days. Every inquiry is reviewed personally.
              </p>

              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <div>
                  <label className="block text-[#1a1a1a] text-sm font-medium mb-1.5">Name</label>
                  <input
                    type="text"
                    data-testid="contact-name"
                    value={form.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Your full name"
                    className={errors.name ? inputError : inputNormal}
                  />
                </div>

                <div>
                  <label className="block text-[#1a1a1a] text-sm font-medium mb-1.5">Email</label>
                  <input
                    type="email"
                    data-testid="contact-email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="Email address"
                    className={errors.email ? inputError : inputNormal}
                  />
                </div>

                <div>
                  <label className="block text-[#1a1a1a] text-sm font-medium mb-1.5">Affiliation</label>
                  <input
                    type="text"
                    data-testid="contact-affiliation"
                    value={form.affiliation}
                    onChange={(e) => handleChange('affiliation', e.target.value)}
                    placeholder="Firm, fund, or family office, if applicable"
                    className={inputNormal}
                  />
                </div>

                <div>
                  <label className="block text-[#1a1a1a] text-sm font-medium mb-1.5">In what capacity</label>
                  <select
                    data-testid="contact-capacity"
                    value={form.capacity}
                    onChange={(e) => handleChange('capacity', e.target.value)}
                    className={`${errors.capacity ? inputError : inputNormal} appearance-none`}
                  >
                    <option value="" disabled>Select one</option>
                    {capacityOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[#1a1a1a] text-sm font-medium mb-1.5">What occasions this introduction</label>
                  <textarea
                    data-testid="contact-subject"
                    value={form.subject}
                    onChange={(e) => handleChange('subject', e.target.value)}
                    placeholder="A few lines on what prompts the conversation. Held in confidence."
                    rows={4}
                    className={inputNormal}
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  data-testid="contact-submit"
                  className="w-full bg-[#1a7a7a] text-white font-bold py-4 rounded-lg hover:bg-[#1a6868] transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {status === 'loading' ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                  Request an Introduction
                </button>

                <p className="text-gray-400 text-xs text-center">
                  Initial conversations are complimentary and held without obligation.
                </p>
              </form>
            </>
          )}
        </div>

        <div className="text-center mt-6">
          <p
            className="text-gray-400 text-xs tracking-wide"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            By appointment &nbsp;&middot;&nbsp; Confidential &nbsp;&middot;&nbsp; Without obligation
          </p>
        </div>
      </div>
    </section>
  );
}
